import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface FieldProps {
  label: ReactNode;
  /** Id of the control, so the label focuses it. */
  htmlFor?: string;
  /** Helper line under the control: an example, a unit, what the value is used for. */
  hint?: ReactNode;
  /** Validation message; replaces the hint while present. */
  error?: ReactNode;
  required?: boolean;
  /** Small text right of the label ("Opcional", a character counter…). */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * A labelled form control. Labels are foreground and medium weight — the question being asked — and
 * the hint underneath explains it in plain words. Use it instead of a bare `<Label>` + `<Input>`.
 */
export function Field({ label, htmlFor, hint, error, required, aside, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
          {label}
          {required && (
            <span className="ml-0.5 text-destructive" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {aside && <span className="shrink-0 text-xs text-muted-foreground">{aside}</span>}
      </div>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-pretty text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
